import { useId } from 'react';

import { Icons } from './icons';
import styles from './SearchField.module.css';

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Names the field for assistive technology (also the placeholder unless `placeholder` is set). */
  label: string;
  placeholder?: string;
  /** The label of the clear button. */
  clearLabel: string;
  testId?: string;
}

/**
 * A compact search input for lists and journals: icon, a native `type="search"` field, and an own
 * clear button (the native one is hidden, it differs between browsers and has no label).
 */
export function SearchField({ value, onChange, label, placeholder, clearLabel, testId }: Props) {
  const id = useId();
  return (
    <div className={styles.root} role="search" data-testid={testId}>
      <span className={styles.icon} aria-hidden>
        <Icons.search />
      </span>
      <input
        id={id}
        type="search"
        className={styles.input}
        value={value}
        placeholder={placeholder ?? label}
        aria-label={label}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          // Escape clears a filled field first; an empty one lets the dialog / drawer close
          if (event.key === 'Escape' && value !== '') {
            event.stopPropagation();
            onChange('');
          }
        }}
      />
      {value !== '' && (
        <button
          type="button"
          className={styles.clear}
          aria-label={clearLabel}
          title={clearLabel}
          onClick={() => {
            onChange('');
            document.getElementById(id)?.focus();
          }}
        >
          <Icons.close />
        </button>
      )}
    </div>
  );
}
